// ============================================================================
// MealForecastReportScreen — Reports → Meal Forecast (buffet check-off slice 4)
// ============================================================================
// Expected covers per date and meal for a property: in-house guests (actual
// guests, departures, MAP by the day's choice / stay preference / plan default),
// confirmed arrivals, tentative bookings, meal add-ons; MAP guests who said
// "either" as may-come; what the restaurant has already marked served. The same
// forecast the mobile Meals tab shows (crs-api MealForecast).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell } from 'components/enterprise-shell';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption } from 'components/ui';
import { mealForecastService } from 'api/services/mealForecastService';
import { propertyService } from 'api/services/propertyService';
import notificationService from 'services/notificationService';
import { downloadCsv } from 'utils/csvExport';
import { forecastRows, forecastTotals, type MealForecastRow } from 'utils/mealForecast';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { usePropertyLabel } from 'hooks/api/usePropertyLabel';
import type { MealForecast, MealForecastExtraItem } from 'api/types/mealForecast.types';
import 'components/modules/dashboard-module/dashboard.css';

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const DAY_OPTIONS = [1, 3, 7, 10, 14].map((d) => ({ value: String(d), label: d === 1 ? '1 day' : `${d} days` }));
const muted: React.CSSProperties = { color: 'var(--text-muted)' };

interface Props {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** RBAC — {'view','export'}. Fail-closed: [] renders no export. */
  allowedActions?: string[];
}

type ExtraRow = MealForecastExtraItem & { id: string; date: string };

const MealForecastReportScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const [propertyId, setPropertyId] = useState('');
  const { defaultPropertyId } = useDefaultProperty();
  useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);
  /** '' = from the property's business date. */
  const [from, setFrom] = useState('');
  const [days, setDays] = useState('7');
  const [view, setView] = useState<'covers' | 'extras'>('covers');
  const [filterOpen, setFilterOpen] = useState(false);
  const propertyLabelsRef = useRef<Record<string, string>>({});
  const fetchedPropertyLabel = usePropertyLabel(propertyId);
  const propertyLabel = fetchedPropertyLabel || propertyLabelsRef.current[propertyId] || '';

  const [forecast, setForecast] = useState<MealForecast | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!propertyId) { setForecast(null); return; }
    setLoading(true);
    setError(null);
    try {
      setForecast(await mealForecastService.getForecast(propertyId, from || null, Number(days)));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'The forecast did not load.');
    } finally {
      setLoading(false);
    }
  }, [propertyId, from, days]);
  useEffect(() => { void load(); }, [load]);

  const loadProperties = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 500, includeInactive: false });
    const opts = (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
    opts.forEach((o: SelectOption) => { propertyLabelsRef.current[o.value as string] = o.label; });
    return opts;
  }, []);

  const rows = useMemo(() => forecastRows(forecast), [forecast]);
  const totals = useMemo(() => forecastTotals(forecast), [forecast]);
  const extras = useMemo<ExtraRow[]>(() => (forecast?.series ?? [])
    .flatMap((d) => d.extraItems.map((x, i) => ({ ...x, id: `${d.date}-${i}`, date: d.date }))), [forecast]);
  const businessDay = forecast?.businessDate?.slice(0, 10);

  const columns = useMemo<SyutGridColumn<MealForecastRow>[]>(() => [
    {
      id: 'date', header: 'Date', accessorKey: 'date', size: 140,
      cell: ({ row }) => <span>{formatDate(row.original.date)}{row.original.date.slice(0, 10) === businessDay ? <span className="badge badge-info" style={{ fontSize: '0.6rem', marginLeft: 6 }}>today</span> : null}</span>,
    },
    { id: 'meal', header: 'Meal', accessorKey: 'meal', size: 90 },
    { id: 'inHouse', header: 'In-house', accessorKey: 'inHouse', size: 90, cell: ({ row }) => row.original.inHouseText },
    { id: 'arriving', header: 'Arriving', accessorKey: 'arriving', size: 90, cell: ({ row }) => row.original.arriving ? row.original.arrivingText : <span style={muted}>—</span> },
    { id: 'tentative', header: 'Tentative', accessorKey: 'tentative', size: 90, cell: ({ row }) => row.original.tentative ? row.original.tentativeText : <span style={muted}>—</span> },
    { id: 'extra', header: 'Extra', accessorKey: 'extra', size: 70, cell: ({ row }) => row.original.extra || <span style={muted}>—</span> },
    { id: 'total', header: 'Covers', accessorKey: 'total', size: 80, cell: ({ row }) => <b>{row.original.total}</b> },
    {
      id: 'mayCome', header: 'May come', accessorKey: 'mayCome', size: 90,
      cell: ({ row }) => row.original.mayCome ? <span title="MAP guests who chose either lunch or dinner — not in the covers">+{row.original.mayCome}</span> : <span style={muted}>—</span>,
    },
    { id: 'served', header: 'Served', accessorKey: 'served', size: 80, cell: ({ row }) => row.original.served || <span style={muted}>—</span> },
    { id: 'plans', header: 'By plan', accessorKey: 'plans', size: 220, cell: ({ row }) => <span title="* = MAP, counted at the guest's chosen meal">{row.original.plans || '—'}</span> },
  ], [businessDay]);

  const extraColumns = useMemo<SyutGridColumn<ExtraRow>[]>(() => [
    { id: 'date', header: 'Date', accessorKey: 'date', size: 140, cell: ({ row }) => formatDate(row.original.date) },
    { id: 'itemName', header: 'Item', accessorKey: 'itemName', size: 180 },
    { id: 'mealType', header: 'In the count of', accessorKey: 'mealType', size: 130, cell: ({ row }) => row.original.mealType ?? <span style={muted}>separate service</span> },
    { id: 'pax', header: 'Pax', accessorKey: 'pax', size: 70 },
    { id: 'bookingNumber', header: 'Booking', accessorKey: 'bookingNumber', size: 140 },
    { id: 'groupName', header: 'Guest / group', accessorKey: 'groupName', size: 180, cell: ({ row }) => row.original.groupName || '—' },
    { id: 'remarks', header: 'Remarks', accessorKey: 'remarks', size: 200, cell: ({ row }) => row.original.remarks || '' },
  ], []);

  const exportCsv = useCallback(() => {
    if (!forecast) { notificationService.toast.warning('Nothing to export yet.'); return; }
    downloadCsv(`MealForecast_${forecast.fromDate.slice(0, 10)}_${forecast.days}d.csv`,
      ['Date', 'Meal', 'In-house', 'Arriving', 'Tentative', 'Extra', 'Covers', 'May come', 'Served', 'By plan'],
      rows.map((r) => [r.date.slice(0, 10), r.meal, r.inHouseText, r.arrivingText, r.tentativeText, r.extra, r.total, r.mayCome, r.served, r.plans]));
  }, [forecast, rows]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const b: ActionButton[] = [
      { id: 'view', icon: <IconSystem.list size={16} />, label: view === 'covers' ? `Additional items (${extras.length})` : 'Show covers', onClick: () => setView((v) => (v === 'covers' ? 'extras' : 'covers')), variant: 'secondary' },
      { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => { void load(); }, variant: 'icon' },
    ];
    if (allowedActions.includes('export'))
      b.push({ id: 'export', icon: <IconSystem.download size={16} />, label: 'Export', tooltip: 'Export to CSV', onClick: exportCsv, variant: 'icon' });
    return b;
  }, [view, extras.length, load, allowedActions, exportCsv]);

  const firstDay = forecast?.series[0]?.date;
  const lastDay = forecast?.series[forecast.series.length - 1]?.date;
  const headerItems: ReportHeaderItem[] = [
    { label: 'Property', value: propertyLabel || (propertyId ? '…' : 'Select in filters'), primary: true },
    { label: 'Period', value: firstDay ? (firstDay === lastDay ? formatDate(firstDay) : `${formatDate(firstDay)} – ${formatDate(lastDay)}`) : '—', primary: true },
    ...(forecast ? [
      { label: 'Business date', value: formatDate(forecast.businessDate), title: 'Its cards carry what the restaurant has already marked served' },
      { label: 'Breakfast', value: totals.Breakfast, icon: 'users' as const },
      { label: 'Lunch', value: totals.Lunch, icon: 'users' as const },
      { label: 'Dinner', value: totals.Dinner, icon: 'users' as const },
    ] : []),
  ];

  const footer = forecast ? (
    <>
      <span>Covers = in-house + arriving + tentative + extra. <b>May come</b>: MAP guests who chose either meal, not counted.</span>
      <span>In-house MAP guests count at the day&rsquo;s choice, else the stay&rsquo;s preference, else the plan&rsquo;s meal (In-House Guests → Meals).</span>
      {forecast.series.some((d) => d.inHouseOnEpPax > 0) ? (
        <span>Room only (EP) in house: {forecast.series.map((d) => d.inHouseOnEpPax).join(' / ')}</span>
      ) : null}
    </>
  ) : undefined;

  return (
    <EnterpriseScreenShell
      title="Meal Forecast"
      description="Expected covers per date and meal: in-house, arriving, tentative, extra — the same forecast as the mobile app."
      icon={<IconSystem.users size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount: (from || days !== '7') ? 1 : 0 }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label={<>Property <span className="text-danger">*</span></>} value={propertyId}
                  onChange={(v) => setPropertyId((v as string) || '')} isAsync loadOptions={loadProperties} defaultOptions cacheOptions={false}
                  isClearable={false} menuPortalTarget={document.body} size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From</label>
                <input type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>Blank: the property&rsquo;s business date.</div>
              </div>
              <div className="filter-section">
                <SyutSelect label="Days" value={days} options={DAY_OPTIONS} onChange={(v) => setDays((v as string) || '7')}
                  isClearable={false} menuPortalTarget={document.body} size="sm" />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setFrom(''); setDays('7'); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody footer={footer}>
        {!propertyId ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>Pick a property in the filters.</div>
        ) : error ? (
          <div style={{ color: 'var(--danger, #ef4444)', padding: '2rem', textAlign: 'center' }}>Failed to load — {error}</div>
        ) : view === 'covers' ? (
          <SyutGrid data={rows} columns={columns} isLoading={loading} emptyMessage="No plan guests, arrivals or meal add-ons in the period." />
        ) : (
          <SyutGrid data={extras} columns={extraColumns} isLoading={loading} emptyMessage="No meal add-ons in the period." />
        )}
      </ReportBody>
    </EnterpriseScreenShell>
  );
};

export default MealForecastReportScreen;
